import type { DramaCategory, DramaSeries } from "~/api/drama";

/** Broad buckets rather than genres: a chip or tab reading "General" tells a visitor nothing. */
const NOT_GENRES = new Set(["general", "drama"]);

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The series' tags that name a category, as the category spells it, in tag order and once each.
 *
 * Raw tags carry spelling variants ("Urban-Romance" beside "Urban Romance") and labels that are not
 * genres at all ("AI Realistic"); the category list is the only clean vocabulary the API has.
 */
export function seriesGenres(series: DramaSeries, categories: DramaCategory[]): string[] {
  const byKey = new Map(categories.map(category => [normalize(category.name), category.name]));
  const genres: string[] = [];
  for (const tag of series.tags ?? []) {
    const key = normalize(tag);
    const name = byKey.get(key);
    if (name && !NOT_GENRES.has(key) && !genres.includes(name)) genres.push(name);
  }
  return genres;
}

/** The genres to show for a series; failing any, its tags that are not broad buckets, once each.
 *  Some series carry only a bucket plus a niche tag ("Vampire") no category names. */
export function displayGenres(series: DramaSeries, categories: DramaCategory[]): string[] {
  const genres = seriesGenres(series, categories);
  if (genres.length > 0) return genres;
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const tag of series.tags ?? []) {
    const key = normalize(tag);
    if (NOT_GENRES.has(key) || seen.has(key)) continue;
    seen.add(key);
    tags.push(tag);
  }
  return tags;
}

/** For a card's one-line label. */
export function primaryGenre(series: DramaSeries, categories: DramaCategory[]): string | undefined {
  return displayGenres(series, categories)[0];
}

/** A tab with one series in it is a dead end, so a category needs this many to get one. */
const MIN_SERIES_PER_TAB = 2;

/** The categories worth a tab, most series first; ties keep the category list's order. */
export function genreTabs(series: DramaSeries[], categories: DramaCategory[]): DramaCategory[] {
  const counts = new Map<string, number>();
  for (const item of series) {
    for (const name of seriesGenres(item, categories)) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return categories
    .filter(category => (counts.get(category.name) ?? 0) >= MIN_SERIES_PER_TAB)
    .sort((a, b) => (counts.get(b.name) ?? 0) - (counts.get(a.name) ?? 0));
}
